import { uid } from "./defaults";
import type { CalibrationProfile, DiceKind, DiceRollState, Point, Scene, TableEdge, WindSpeed } from "../types";
import { windStrength } from "./wind";
import { mediaUrl } from "./media-url";

export const DICE_SIDES: Record<DiceKind, number> = { d4: 4, d6: 6, d8: 8, d10: 10, d12: 12, d20: 20, d100: 100 };
export const DICE_TABLE_BOUNCES = 6;
/** Gives a Player Display time to load the lazy Three.js dice effect before it becomes visible. */
export const DICE_PLAYER_PREPARE_MS = 900;
/** Deliberately cinematic: enough time to read a full roll and its bounces. */
export const DICE_THROW_MS = 3_600;
/** Includes the arm's approach, a moment of contact, and the pull back to the DM. */
export const DICE_RECALL_MS = 2_400;
export const DICE_RECALL_CLEANUP_MS = DICE_RECALL_MS + 125;
export const DICE_RECALL_ARM_ASSET = mediaUrl("/assets/dice/recall-arm-extended.webp");

const DICE_COLORS: Record<DiceKind, string> = {
  d4: "#d8534f", d6: "#2d79c7", d8: "#38a36c", d10: "#8a5bd1", d12: "#d8902f", d20: "#b93468", d100: "#3c687f",
};

/** A throw from one table edge should finish near its opposite edge, not in the centre. */
export function diceOppositeSideTarget(origin: TableEdge, random: () => number): Point {
  const lane = 0.16 + random() * 0.68;
  const farEdge = 0.80 + random() * 0.08;
  if (origin === "north") return { x: lane, y: farEdge };
  if (origin === "south") return { x: lane, y: 1 - farEdge };
  if (origin === "east") return { x: 1 - farEdge, y: lane };
  return { x: farEdge, y: lane };
}

function targetPoint(origin: TableEdge, random: () => number, existing: Point[]): Point {
  let candidate = diceOppositeSideTarget(origin, random);
  for (let attempt = 0; attempt < 12; attempt += 1) {
    if (existing.every((point) => Math.hypot(point.x - candidate.x, point.y - candidate.y) > 0.12)) return candidate;
    candidate = diceOppositeSideTarget(origin, random);
  }
  return candidate;
}

export function createDiceRoll(kind: DiceKind, requestedCount: number, origin: TableEdge, random: () => number = Math.random, commandAt = Date.now()): DiceRollState {
  const count = Math.max(1, Math.min(8, Math.round(requestedCount)));
  const targets: Point[] = [];
  const dice = Array.from({ length: count }, () => {
    const target = targetPoint(origin, random, targets); targets.push(target);
    return {
      id: uid("die"), kind, value: 1 + Math.floor(random() * DICE_SIDES[kind]),
      seed: Math.floor(random() * 0x7fffffff), target, color: DICE_COLORS[kind],
    };
  });
  return { id: uid("dice-roll"), origin, phase: "throw", commandAt, dice };
}

export function diceResultText(kind: DiceKind, value: number) {
  return kind === "d100" ? `${value}%` : String(value);
}

export function diceRollSummary(roll?: DiceRollState) {
  if (!roll?.dice.length) return "";
  const values = roll.dice.map((die) => diceResultText(die.kind, die.value));
  const sameKind = roll.dice.every((die) => die.kind === roll.dice[0].kind);
  const notation = sameKind ? `${roll.dice.length > 1 ? roll.dice.length : ""}${roll.dice[0].kind}` : "Mixed dice";
  const total = roll.dice.reduce((sum, die) => sum + die.value, 0);
  return `${notation}: ${values.join(" + ")}${roll.dice.length > 1 ? ` = ${total}` : ""}`;
}

export interface DiceObstacle extends Point { radius: number; tokenId?: string }

export function diceStartPoint(edge: TableEdge, target: Point, halfWidth: number, halfHeight: number, jitter = 0): Point {
  const outsideMargin = 2.35;
  if (edge === "north") return { x: target.x * 0.55 + jitter, y: halfHeight + outsideMargin };
  if (edge === "south") return { x: target.x * 0.55 + jitter, y: -halfHeight - outsideMargin };
  if (edge === "east") return { x: halfWidth + outsideMargin, y: target.y * 0.55 + jitter };
  return { x: -halfWidth - outsideMargin, y: target.y * 0.55 + jitter };
}

function sceneTransform(scene: Scene, width: number, height: number, calibration?: CalibrationProfile) {
  const mapWidth = scene.geometry?.width ?? 24; const mapHeight = scene.geometry?.height ?? 16;
  const fit = Math.min(width / mapWidth, height / mapHeight);
  const physical = (calibration?.pixelsPerInch ?? 96) * scene.gridInches;
  const scale = Math.max(3, (scene.view.mode === "physical" ? physical : fit) * scene.view.zoom);
  return { scale, offsetX: width / 2 - mapWidth * scale / 2 + scene.view.x * scale, offsetY: height / 2 - mapHeight * scale / 2 + scene.view.y * scale };
}

function tokenScreenPoint(point: Point, scene: Scene, transform: ReturnType<typeof sceneTransform>): Point {
  const geometry = scene.geometry!; let x = point.x; let y = point.y;
  if (scene.view.rotation === 90) { x = geometry.height - point.y; y = point.x; }
  else if (scene.view.rotation === 180) { x = geometry.width - point.x; y = geometry.height - point.y; }
  else if (scene.view.rotation === 270) { x = point.y; y = geometry.width - point.x; }
  return { x: transform.offsetX + x * transform.scale, y: transform.offsetY + y * transform.scale };
}

/** Convert player-safe scene tokens to circles in the dice overlay's world coordinates. */
export function diceTokenObstacles(scene: Scene | undefined, calibration: CalibrationProfile | undefined, width: number, height: number, halfWidth: number, halfHeight: number): DiceObstacle[] {
  if (!scene?.geometry || width <= 0 || height <= 0) return [];
  const transform = sceneTransform(scene, width, height, calibration);
  return scene.tokens.map((token) => {
    const screen = tokenScreenPoint(token.position, scene, transform);
    const screenRadius = Math.max(8, token.size * transform.scale * 0.42) + 3;
    return {
      x: (screen.x / width * 2 - 1) * halfWidth,
      y: (1 - screen.y / height * 2) * halfHeight,
      radius: screenRadius / height * halfHeight * 2,
      tokenId: token.id,
    };
  });
}

function segmentCircleHit(from: Point, to: Point, obstacle: DiceObstacle, clearance: number) {
  const dx = to.x - from.x; const dy = to.y - from.y; const fx = from.x - obstacle.x; const fy = from.y - obstacle.y;
  const a = dx * dx + dy * dy; if (a < 1e-8) return undefined;
  const b = 2 * (fx * dx + fy * dy); const c = fx * fx + fy * fy - clearance * clearance;
  const discriminant = b * b - 4 * a * c; if (discriminant < 0) return undefined;
  const root = Math.sqrt(discriminant); const first = (-b - root) / (2 * a); const second = (-b + root) / (2 * a);
  const hit = [first, second].find((value) => value > 0.012 && value < 0.988);
  return hit;
}

function moveTargetClear(target: Point, obstacles: DiceObstacle[], dieRadius: number, seed: number) {
  const result = { ...target };
  for (let pass = 0; pass < 5; pass += 1) {
    let moved = false;
    for (const obstacle of obstacles) {
      const clearance = obstacle.radius + dieRadius; const dx = result.x - obstacle.x; const dy = result.y - obstacle.y; const distance = Math.hypot(dx, dy);
      if (distance >= clearance + 0.08) continue;
      const angle = distance > 0.001 ? Math.atan2(dy, dx) : (seed % 6283) / 1000;
      result.x = obstacle.x + Math.cos(angle) * (clearance + 0.1); result.y = obstacle.y + Math.sin(angle) * (clearance + 0.1); moved = true;
    }
    if (!moved) break;
  }
  return result;
}

export interface DiceTravelPath { points: Point[]; collisionCount: number }

/** Build an outside-to-table route which visibly deflects around token circles. */
export function buildDiceTravelPath(from: Point, requestedTarget: Point, obstacles: DiceObstacle[], dieRadius: number, seed = 1): DiceTravelPath {
  const target = moveTargetClear(requestedTarget, obstacles, dieRadius, seed); const points: Point[] = [{ ...from }];
  let current = { ...from }; let collisionCount = 0; let lastObstacle = "";
  for (let guard = 0; guard < 18; guard += 1) {
    const hits = obstacles.map((obstacle) => ({ obstacle, hit: segmentCircleHit(current, target, obstacle, obstacle.radius + dieRadius) }))
      .filter((candidate): candidate is { obstacle: DiceObstacle; hit: number } => candidate.hit !== undefined && !(candidate.obstacle.tokenId === lastObstacle && candidate.hit < 0.06))
      .sort((a, b) => a.hit - b.hit);
    const first = hits[0]; if (!first) { points.push(target); break; }
    const direction = { x: target.x - current.x, y: target.y - current.y }; const directionLength = Math.max(0.001, Math.hypot(direction.x, direction.y)); direction.x /= directionLength; direction.y /= directionLength;
    const clearance = first.obstacle.radius + dieRadius + 0.1;
    const hitPoint = { x: current.x + (target.x - current.x) * first.hit, y: current.y + (target.y - current.y) * first.hit };
    const entryAngle = Math.atan2(hitPoint.y - first.obstacle.y, hitPoint.x - first.obstacle.x);
    const targetCross = direction.x * (target.y - first.obstacle.y) - direction.y * (target.x - first.obstacle.x);
    const side = Math.abs(targetCross) > 0.001 ? Math.sign(targetCross) : ((seed + collisionCount) % 2 ? 1 : -1);
    points.push({ x: first.obstacle.x + Math.cos(entryAngle) * clearance, y: first.obstacle.y + Math.sin(entryAngle) * clearance });
    for (let step = 1; step <= 3; step += 1) {
      const angle = entryAngle - side * step * Math.PI / 6;
      points.push({ x: first.obstacle.x + Math.cos(angle) * clearance, y: first.obstacle.y + Math.sin(angle) * clearance });
    }
    current = points[points.length - 1]; lastObstacle = first.obstacle.tokenId ?? String(obstacles.indexOf(first.obstacle)); collisionCount += 1;
  }
  if (points.length === 1 || points[points.length - 1].x !== target.x || points[points.length - 1].y !== target.y) points.push(target);
  return { points, collisionCount };
}

export function pointAlongDicePath(points: Point[], progress: number): Point {
  if (points.length < 2) return points[0] ?? { x: 0, y: 0 };
  const lengths = points.slice(1).map((point, index) => Math.hypot(point.x - points[index].x, point.y - points[index].y));
  const total = lengths.reduce((sum, length) => sum + length, 0); let remaining = Math.max(0, Math.min(1, progress)) * total;
  for (let index = 0; index < lengths.length; index += 1) {
    if (remaining <= lengths[index] || index === lengths.length - 1) {
      const amount = lengths[index] > 0 ? Math.min(1, remaining / lengths[index]) : 1;
      return { x: points[index].x + (points[index + 1].x - points[index].x) * amount, y: points[index].y + (points[index + 1].y - points[index].y) * amount };
    }
    remaining -= lengths[index];
  }
  return points[points.length - 1];
}

/** A small visual-only crosswind which returns to zero before the die settles. */
export function diceWindOffset(progress: number, speed: WindSpeed | undefined, seed: number): Point {
  const bounded = Math.max(0, Math.min(1, progress));
  const strength = windStrength(speed);
  if (!strength || bounded === 0 || bounded === 1) return { x: 0, y: 0 };
  const envelope = Math.pow(Math.sin(bounded * Math.PI), 1.35);
  const phase = (seed % 6_283) / 1_000;
  return {
    x: envelope * strength * (0.055 + Math.sin(phase + bounded * 5.2) * 0.012),
    y: envelope * strength * Math.sin(phase * 1.7 + bounded * 7.1) * 0.018,
  };
}

/**
 * A die begins a touch larger while it is above the table, then eases once to
 * its tabletop size. It must never grow again during bounces or settlement.
 */
export function dicePerspectiveScale(progress: number, landingProgress = 0.60, airborneScale = 1.08) {
  const boundedProgress = Math.max(0, Math.min(1, progress));
  const landing = Math.max(0.2, Math.min(0.8, landingProgress));
  const start = Math.max(1, Math.min(1.16, airborneScale));
  if (boundedProgress >= landing) return 1;
  const t = boundedProgress / landing;
  const easeOut = 1 - Math.pow(1 - t, 2.2);
  return start + (1 - start) * easeOut;
}

function diceLandingProgress(seed = 0) {
  // A small deterministic variation prevents a handful of dice from hitting
  // the table in lockstep, while retaining an exact zero-height final frame.
  if (!seed) return 0.60;
  const unit = ((seed >>> 7) % 101) / 100;
  return 0.60 + (unit - 0.5) * 0.05;
}

/** Physics-inspired, deterministic tabletop contact for a thrown die. */
export function diceTabletopMotion(progress: number, seed = 0) {
  const bounded = Math.max(0, Math.min(1, progress)); const landing = diceLandingProgress(seed);
  if (bounded <= landing || bounded >= 1) return { height: 0, offset: { x: 0, y: 0 }, landing, bounce: 0 };
  const settled = (bounded - landing) / (1 - landing); const phase = settled * DICE_TABLE_BOUNCES;
  const bounce = Math.min(DICE_TABLE_BOUNCES - 1, Math.floor(phase)); const local = phase - Math.floor(phase);
  const restitution = Math.pow(0.64, bounce); const pulse = Math.sin(Math.max(0, Math.min(1, local)) * Math.PI);
  const angle = ((seed % 6_283) / 1_000) + bounce * 2.13;
  // The sideways hop and tiny fore/aft skid read as a die rolling from one
  // table contact to the next instead of hopping vertically in place.
  const sideways = pulse * (0.18 * restitution); const skid = Math.sin(local * Math.PI * 2) * (0.045 * restitution);
  return {
    height: pulse * 0.80 * restitution,
    offset: { x: Math.cos(angle) * sideways - Math.sin(angle) * skid, y: Math.sin(angle) * sideways + Math.cos(angle) * skid },
    landing,
    bounce: bounce + 1,
  };
}

/** Keep visual bounce offsets from passing through a token's occupied circle. */
export function keepDiceClearOfObstacles(position: Point, obstacles: DiceObstacle[], dieRadius: number, seed = 0): Point {
  const result = { ...position };
  for (let pass = 0; pass < 4; pass += 1) {
    let adjusted = false;
    for (const obstacle of obstacles) {
      const clearance = obstacle.radius + dieRadius; const dx = result.x - obstacle.x; const dy = result.y - obstacle.y; const distance = Math.hypot(dx, dy);
      if (distance >= clearance) continue;
      const angle = distance > 0.0001 ? Math.atan2(dy, dx) : (seed % 6_283) / 1_000;
      result.x = obstacle.x + Math.cos(angle) * clearance; result.y = obstacle.y + Math.sin(angle) * clearance; adjusted = true;
    }
    if (!adjusted) break;
  }
  return result;
}

/**
 * Once a die has crossed onto the projected tabletop, keep its complete
 * footprint on the display. Obstacle avoidance and a bounce near an edge can
 * otherwise nudge it outside the canvas before its final settling point.
 */
export function keepDiceOnTabletop(position: Point, halfWidth: number, halfHeight: number, dieRadius: number): Point {
  const inset = Math.max(0.02, dieRadius);
  const maxX = Math.max(0, halfWidth - inset); const maxY = Math.max(0, halfHeight - inset);
  return {
    x: Math.max(-maxX, Math.min(maxX, position.x)),
    y: Math.max(-maxY, Math.min(maxY, position.y)),
  };
}

/** Vertical component of the irregular, diminishing tabletop bounce. */
export function diceBounceHeight(progress: number) {
  return diceTabletopMotion(progress).height;
}

/** Approximate a 2×2 map-grid footprint in the dice overlay's world scale. */
export function diceApexScaleForGrid(gridPixels: number, viewportHeight: number, baseScale = 1) {
  if (!Number.isFinite(gridPixels) || gridPixels <= 0 || !Number.isFinite(viewportHeight) || viewportHeight <= 0) return 0.56;
  const settledDiameterPixels = 1.64 * (viewportHeight / 10) * baseScale;
  return Math.max(0.34, Math.min(0.82, gridPixels * 2 / settledDiameterPixels));
}
